const toDateOnly = (value) => {
  if (!value) return null;
  // Prefer YYYY-MM-DD local midnight to avoid UTC timezone off-by-one.
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [y, m, d] = value.slice(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const isActiveFlag = (value) => value === 1 || value === true || value === "1";

const isWithinDateRange = (item, today = new Date()) => {
  const start = toDateOnly(item.start_date || item.startDate);
  const end = toDateOnly(
    item.end_date || item.end_dtae || item.endDate || item.endDtae,
  );
  const current = new Date(today);
  current.setHours(0, 0, 0, 0);

  if (start && current < start) return false;
  if (end && current > end) return false;
  return true;
};

/**
 * Axios interceptor returns `response.data` already.
 * Support both:
 *   { status, title, images }
 *   { data: { status, title, images } }
 *   axios-style { data: ... } if interceptor is bypassed
 *   [{ status, title, images }, ...] — multiple campaigns
 */
const getAdsPayload = (response) => {
  if (!response || typeof response !== "object") return null;

  if (Array.isArray(response.images)) {
    return response;
  }

  if (response.data && typeof response.data === "object") {
    if (Array.isArray(response.data.images)) {
      return response.data;
    }
    if (
      response.data.data &&
      typeof response.data.data === "object" &&
      Array.isArray(response.data.data.images)
    ) {
      return response.data.data;
    }
  }

  // Fallback: payload at root with status/title but empty/missing images handled later
  if (response.status != null || response.title != null) {
    return response;
  }

  return null;
};

const isEligibleImage = (item, today) => {
  if (!item?.image_url) return false;
  if (!isActiveFlag(item.isActive ?? item.is_active)) return false;
  return isWithinDateRange(item, today);
};

/**
 * Prefer SPA navigation when redirect points at this origin.
 */
const resolveRedirect = (url) => {
  if (!url || typeof url !== "string") {
    return { redirectType: "external", redirectUrl: null, openInNewTab: false };
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return { redirectType: "external", redirectUrl: null, openInNewTab: false };
  }

  if (trimmed.startsWith("/")) {
    return {
      redirectType: "internal",
      redirectUrl: trimmed,
      openInNewTab: false,
    };
  }

  const withProtocol = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const parsed = new URL(withProtocol);
    if (
      typeof window !== "undefined" &&
      window.location?.origin &&
      parsed.origin === window.location.origin
    ) {
      return {
        redirectType: "internal",
        redirectUrl: `${parsed.pathname}${parsed.search}${parsed.hash}`,
        openInNewTab: false,
      };
    }
  } catch {
    // fall through
  }

  return {
    redirectType: "external",
    redirectUrl: withProtocol,
    openInNewTab: true,
  };
};

const findByType = (images, type) =>
  images.find((item) => String(item.type || "").toLowerCase() === type) || null;

const getAdGroupKey = (item) =>
  item.campaign_id ||
  item.campaignId ||
  item.ad_id ||
  item.adId ||
  item.group_id ||
  item.groupId ||
  null;

const getImageType = (item) => String(item?.type || "").toLowerCase();

const buildPromoAd = (desktopImage, mobileImage, payload = {}) => {
  if (!desktopImage?.image_url) return null;

  const mobile = mobileImage || desktopImage;
  const redirectSource =
    desktopImage.redirectional_url ||
    mobile?.redirectional_url ||
    desktopImage.redirect_url ||
    "";
  const { redirectType, redirectUrl, openInNewTab } =
    resolveRedirect(redirectSource);

  return {
    adId:
      desktopImage.id ||
      desktopImage.ad_id ||
      desktopImage.campaign_id ||
      "dashboard-ad",
    imageUrl: desktopImage.image_url,
    imageUrlMobile: mobile?.image_url || desktopImage.image_url,
    redirectType,
    redirectUrl,
    openInNewTab,
    altText: payload.title || "Advertisements",
    title: payload.title || "",
    summary: payload.summary || "",
  };
};

const parseSingleCampaignAds = (response) => {
  const payload = getAdsPayload(response);
  if (!payload || payload.status === false) {
    return [];
  }

  const today = new Date();
  const images = Array.isArray(payload.images) ? payload.images : [];
  const eligible = images.filter((item) => isEligibleImage(item, today));

  if (!eligible.length) {
    return [];
  }

  const hasGroupKeys = eligible.some((item) => getAdGroupKey(item));

  if (hasGroupKeys) {
    const groups = new Map();
    eligible.forEach((item, index) => {
      const key = getAdGroupKey(item) || `ungrouped-${index}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });

    return Array.from(groups.values())
      .map((items) => {
        const webImage = findByType(items, "web");
        const appImage = findByType(items, "app");
        const desktopImage = webImage || appImage || items[0];
        const mobileImage = appImage || webImage || items[0];
        return buildPromoAd(desktopImage, mobileImage, payload);
      })
      .filter(Boolean);
  }

  const webImages = eligible.filter((item) => getImageType(item) === "web");
  const appImages = eligible.filter((item) => getImageType(item) === "app");
  const otherImages = eligible.filter((item) => {
    const type = getImageType(item);
    return type !== "web" && type !== "app";
  });

  if (webImages.length > 0) {
    return webImages
      .map((webImage, index) => {
        const matchedApp =
          appImages.find(
            (app) =>
              getAdGroupKey(app) &&
              getAdGroupKey(app) === getAdGroupKey(webImage),
          ) ||
          (webImages.length === appImages.length
            ? appImages[index]
            : null) ||
          (webImages.length === 1 ? appImages[0] : null);

        return buildPromoAd(webImage, matchedApp || webImage, payload);
      })
      .filter(Boolean);
  }

  if (appImages.length > 0) {
    return appImages
      .map((appImage) => buildPromoAd(appImage, appImage, payload))
      .filter(Boolean);
  }

  return otherImages
    .map((item) => buildPromoAd(item, item, payload))
    .filter(Boolean);
};

/**
 * Parses `/dashboard/ads` into one or more banner slides.
 * - Groups web/app pairs into a single slide when possible
 * - Multiple web images (or campaign groups) become multiple slides
 * - Supports an array of campaign payloads
 */
export const parsePromoAds = (response) => {
  if (Array.isArray(response)) {
    return response.flatMap((item) => parsePromoAds(item));
  }

  if (
    response?.data &&
    Array.isArray(response.data) &&
    !Array.isArray(response.images) &&
    !Array.isArray(response.data.images)
  ) {
    return response.data.flatMap((item) => parsePromoAds(item));
  }

  return parseSingleCampaignAds(response);
};

/**
 * Parses `/dashboard/ads` into one banner (first eligible slide).
 * - desktop → type "web" image
 * - mobile viewport → type "app" image (fallback to web)
 */
export const parsePromoAd = (response) => {
  const ads = parsePromoAds(response);
  return ads[0] || null;
};
